// URL <-> filter-state contract for the proposals page (deep links, canonical
// segments and the watched query surface). Extracted verbatim from RentFlowsIndex.vue.
import { normalizeActivityFilter } from './offerFilterUtils';
import {
  normalizeRentProgressContractPeriod,
  normalizeRentProgressContractStatus,
  normalizeRentProgressDocumentationPeriod,
  normalizeRentProgressPhase,
} from './rentProgressPhasePresenter';

export const routeValue = value => (Array.isArray(value) ? value[0] : value);

export const routeString = (value, fallback = '') =>
  String(routeValue(value) || fallback);

export const normalizedRouteText = value =>
  String(routeValue(value) || '')
    .trim()
    .toLowerCase();

export const normalizedFromList = (value, allowedValues, fallback = 'all') => {
  const normalized = routeString(value, fallback);
  return allowedValues.includes(normalized) ? normalized : fallback;
};

export const initialOfferBucketFromRoute = route => {
  const status = normalizedRouteText(
    route.params.offerStatus ||
      route.query.status_bucket ||
      route.query.bucket ||
      route.query.situacao ||
      route.query.status
  );
  if (
    [
      'accepted',
      'finished',
      'finalizada',
      'finalizadas',
      'concluidas',
      'concluídas',
      'aceitas',
    ].includes(status)
  ) {
    return 'finished';
  }
  if (
    ['declined', 'withdrawn', 'canceled', 'canceladas', 'cancelled'].includes(
      status
    )
  ) {
    return 'canceled';
  }
  return 'in_negotiation';
};

export const initialNegotiationStatusFromRoute = route => {
  const status = normalizedRouteText(route.query.status);
  if (
    ['submitted', 'recebidas', 'recebida', 'para_responder'].includes(status)
  ) {
    return 'submitted';
  }
  if (status === 'accepted') return 'accepted';
  return 'all';
};

export const initialActivityFilterFromRoute = route =>
  normalizeActivityFilter({
    months: routeString(route.query.activity_months),
    startDate: routeString(route.query.submitted_from),
    endDate: routeString(route.query.submitted_to),
  });

export const initialSortFromRoute = (route, offerSortValues) =>
  normalizedFromList(route.query.order_by, offerSortValues, 'recent_activity');

export const initialContactIdFromRoute = route =>
  routeString(route.query.contact_id || route.query.client_id);

export const initialPropertyIdFromRoute = route =>
  routeString(
    route.query.imovel_id ||
      route.query.house_id ||
      route.query.houseId ||
      route.query.property_id
  );

export const initialRentProgressPhaseFromRoute = route =>
  normalizeRentProgressPhase(
    route.params.rentProgressPhase ||
      route.query.rent_progress_phase ||
      route.query.rentProgressPhase ||
      route.query.phase ||
      route.query.tab
  );

export const initialRentProgressContractPeriodFromRoute = route =>
  normalizeRentProgressContractPeriod(
    route.query.contract_period ||
      route.query.contractPeriod ||
      route.query['contracts-filters'] ||
      route.query.contracts_filter
  );

export const initialRentProgressContractStatusFromRoute = route =>
  normalizeRentProgressContractStatus(
    route.query.contract_status ||
      route.query.contractStatus ||
      route.query['contracts-filters-status'] ||
      route.query.contracts_status
  );

export const initialRentProgressDocumentationPeriodFromRoute = route =>
  normalizeRentProgressDocumentationPeriod(
    route.query.documentation_period ||
      route.query.documentationPeriod ||
      route.query['documentations-filters'] ||
      route.query.documentations_filter
  );

export const compactQuery = query =>
  Object.fromEntries(
    Object.entries(query).filter(([, value]) => value !== '' && value != null)
  );

export const offerBucketRouteSegment = value =>
  ({
    in_negotiation: 'em-andamento',
    finished: 'finalizadas',
    canceled: 'canceladas',
  })[value] || 'em-andamento';

export const rentProgressPhaseRouteSegment = value =>
  ({
    negotiation: 'negociacao',
    documentation: 'documentacao',
    contract: 'contratos',
  })[normalizeRentProgressPhase(value)] || 'negociacao';

// Every query/param key that must re-apply the filter state when it changes.
export const offerRouteWatchSources = route => [
  route.name,
  route.path,
  route.params.offerStatus,
  route.params.rentProgressPhase,
  route.query.status_bucket,
  route.query.rent_progress_phase,
  route.query.rentProgressPhase,
  route.query.phase,
  route.query.tab,
  route.query.contract_period,
  route.query.contractPeriod,
  route.query['contracts-filters'],
  route.query.contracts_filter,
  route.query.contract_status,
  route.query.contractStatus,
  route.query['contracts-filters-status'],
  route.query.contracts_status,
  route.query.documentation_period,
  route.query.documentationPeriod,
  route.query['documentations-filters'],
  route.query.documentations_filter,
  route.query.bucket,
  route.query.situacao,
  route.query.status,
  route.query.business_context,
  route.query.context,
  route.query.tipo,
  route.query.finalidade,
  route.query.kind,
  route.query.payment_kind,
  route.query.activity_months,
  route.query.submitted_from,
  route.query.submitted_to,
  route.query.q,
  route.query.contact_id,
  route.query.client_id,
  route.query.imovel_id,
  route.query.house_id,
  route.query.houseId,
  route.query.property_id,
  route.query.order_by,
  route.query.offer_id,
  route.query.property_offer_id,
  route.query.sales_flow_id,
  route.query.booking_reference,
  route.query.token,
  route.query.screen,
];

export const buildOfferRouteSyncTarget = ({
  route,
  accountId,
  offerBucket,
  rentProgressPhase,
  rentProgressContractPeriod,
  rentProgressContractStatus,
  rentProgressDocumentationPeriod,
  negotiationStatus,
  filterContext,
  filterKind,
  filterPaymentKind,
  filterActivityMonths,
  filterActivityStartDate,
  filterActivityEndDate,
  searchTerm,
  // Entity filters (contact_id / imovel_id) are echoed verbatim into the URL.
  entityFilters = {},
  sortBy,
}) => {
  let routeName = 'lais_tenants_rent_flows';
  if (offerBucket === 'in_negotiation' && rentProgressPhase !== 'negotiation') {
    routeName = 'lais_tenants_rent_flows_status_phase';
  } else if (route.name === 'lais_tenants_rent_flows_status') {
    routeName = 'lais_tenants_rent_flows_status';
  }

  const params = { accountId: route.params.accountId || accountId };
  if (
    routeName === 'lais_tenants_rent_flows_status' ||
    routeName === 'lais_tenants_rent_flows_status_phase'
  ) {
    params.offerStatus = offerBucketRouteSegment(offerBucket);
  }
  if (routeName === 'lais_tenants_rent_flows_status_phase') {
    params.rentProgressPhase = rentProgressPhaseRouteSegment(rentProgressPhase);
  }

  return {
    name: routeName,
    params,
    query: compactQuery({
      status_bucket: offerBucket,
      rent_progress_phase:
        routeName !== 'lais_tenants_rent_flows_status_phase' &&
        offerBucket === 'in_negotiation' &&
        rentProgressPhase !== 'negotiation'
          ? rentProgressPhase
          : '',
      contract_period:
        offerBucket === 'in_negotiation' &&
        rentProgressPhase === 'contract' &&
        rentProgressContractPeriod !== 'LAST_30_DAYS'
          ? rentProgressContractPeriod
          : '',
      contract_status:
        offerBucket === 'in_negotiation' &&
        rentProgressPhase === 'contract' &&
        rentProgressContractStatus !== 'ALL'
          ? rentProgressContractStatus
          : '',
      documentation_period:
        offerBucket === 'in_negotiation' &&
        rentProgressPhase === 'documentation' &&
        rentProgressDocumentationPeriod !== 'LAST_30_DAYS'
          ? rentProgressDocumentationPeriod
          : '',
      status:
        offerBucket === 'in_negotiation' && negotiationStatus !== 'all'
          ? negotiationStatus
          : '',
      business_context: filterContext === 'all' ? '' : filterContext,
      kind: filterKind === 'all' ? '' : filterKind,
      payment_kind: filterPaymentKind === 'all' ? '' : filterPaymentKind,
      activity_months: filterActivityMonths,
      submitted_from: filterActivityStartDate,
      submitted_to: filterActivityEndDate,
      q: searchTerm.trim(),
      ...entityFilters,
      order_by: sortBy === 'recent_activity' ? '' : sortBy,
      offer_id: routeValue(route.query.offer_id),
      property_offer_id: routeValue(route.query.property_offer_id),
      sales_flow_id: routeValue(route.query.sales_flow_id),
      booking_reference: routeValue(route.query.booking_reference),
      token: routeValue(route.query.token),
      screen: routeValue(route.query.screen),
    }),
  };
};

export const offerDetailsRouteIdentifier = route => {
  const fromQuery = [
    route.query.offer_id,
    route.query.property_offer_id,
    route.query.sales_flow_id,
    route.query.booking_reference,
    route.query.token,
  ]
    .map(value => routeString(value).trim())
    .find(Boolean);
  if (fromQuery) return fromQuery;

  const segment = routeString(route.params.offerStatus).trim();
  return /^\d+$/.test(segment) ? segment : '';
};

export const offerIdentifierMatches = (offer, identifier) => {
  if (!offer || !identifier) return false;
  const normalized = String(identifier);
  const attrs = offer.metadata || offer.additional_attributes || {};
  return [
    offer.id,
    offer.booking_reference,
    offer.sales_flow_id,
    attrs.sales_flow_id,
    attrs.salesFlowId,
  ]
    .filter(value => value !== undefined && value !== null && value !== '')
    .some(value => String(value) === normalized);
};
